'use client';

import { useSyncExternalStore } from 'react';

/**
 * Utvecklingsläget — inloggning utan konto, och framtvingade svarstillstånd.
 *
 * Finns för att kunna granska gränssnittet på riktigt. En polerad vy måste
 * kontrolleras i alla sina tillstånd (laddar, tomt, fel, långsamt) och för varje
 * behörighet som når den. Med en riktig databas går det inte: man kan inte
 * beställa fram "en nyskapad förening utan innehåll" eller "anropet gick fel"
 * utan att förstöra data, och man ser bara appen genom de roller ens eget konto
 * råkar ha.
 *
 * Läget slås på med `NEXT_PUBLIC_AUTH_MODE=dev` och är dessutom hårdspärrat mot
 * produktionsbyggen — båda villkoren måste vara uppfyllda.
 */

/** Sant bara i utvecklingsbygge OCH med flaggan satt. Aldrig i produktion. */
export const DEV_LÄGE_AKTIVT =
  process.env.NODE_ENV !== 'production' && process.env.NEXT_PUBLIC_AUTH_MODE === 'dev';

/**
 * Vilket svar backend ska låtsas ge.
 *
 * - `data` — verkliga anrop går fram som vanligt (standard).
 * - `laddar` — anropen svarar aldrig, så laddtillståndet går att titta på.
 * - `tom` — svaren är tomma, för tomtillstånden.
 * - `fel` — anropen misslyckas med 500, för feltillstånden.
 * - `langsam` — svaren fördröjs, för att se om något hinner blinka eller hoppa.
 *
 * Värdena är ASCII med flit: de färdas i en frågesträng.
 */
export type Scenario = 'data' | 'laddar' | 'tom' | 'fel' | 'langsam';

export const SCENARIER: Scenario[] = ['data', 'laddar', 'tom', 'fel', 'langsam'];

/** Hur länge `langsam` väntar innan svaret släpps fram. */
export const LÅNGSAM_FÖRDRÖJNING_MS = 3000;

const SCENARIO_NYCKEL = 'dev-scenario';
const ROLL_NYCKEL = 'dev-roller';

export const STANDARDROLLER = ['styrelseledamot'];

function ärScenario(värde: unknown): värde is Scenario {
  return typeof värde === 'string' && (SCENARIER as string[]).includes(värde);
}

/**
 * En liten modulstore i stället för state i en effekt.
 *
 * Servern och den första renderingen får alltid standardvärdet, medan läsningen
 * ur URL och sessionStorage sker först när klienten tagit över. Utan den
 * uppdelningen får man antingen hydreringsfel eller en `setState` i en effekt —
 * samma mönster som `useSyncExternalStore` används till på andra håll i appen.
 */
const lyssnare = new Set<() => void>();

function prenumerera(vidÄndring: () => void) {
  lyssnare.add(vidÄndring);
  return () => {
    lyssnare.delete(vidÄndring);
  };
}

function meddela() {
  lyssnare.forEach((vidÄndring) => vidÄndring());
}

// ---------------------------------------------------------------- scenario

let cachatScenario: Scenario | null = null;

export function aktivtScenario(): Scenario {
  if (!DEV_LÄGE_AKTIVT || typeof window === 'undefined') {
    return 'data';
  }
  if (cachatScenario) {
    return cachatScenario;
  }

  try {
    const frånUrl = new URLSearchParams(window.location.search).get('scenario');
    if (ärScenario(frånUrl)) {
      window.sessionStorage.setItem(SCENARIO_NYCKEL, frånUrl);
      cachatScenario = frånUrl;
      return cachatScenario;
    }

    const sparat = window.sessionStorage.getItem(SCENARIO_NYCKEL);
    if (ärScenario(sparat)) {
      cachatScenario = sparat;
      return cachatScenario;
    }
  } catch {
    // Privat läge eller blockerad lagring — kör vidare på verkliga anrop.
  }

  cachatScenario = 'data';
  return cachatScenario;
}

function scenarioServerSnapshot(): Scenario {
  return 'data';
}

export function sättScenario(scenario: Scenario): void {
  if (!DEV_LÄGE_AKTIVT || typeof window === 'undefined') return;
  cachatScenario = scenario;
  try {
    window.sessionStorage.setItem(SCENARIO_NYCKEL, scenario);
  } catch {
    // Valet gäller ändå för den här sidvisningen.
  }
  meddela();
}

export function useScenario(): Scenario {
  return useSyncExternalStore(prenumerera, aktivtScenario, scenarioServerSnapshot);
}

// ------------------------------------------------------------------ roller

/**
 * Rollerna den påhittade användaren har just nu.
 *
 * Namnen måste finnas som nycklar i `ROLL_TILL_BEHÖRIGHETER`
 * (`src/layouts/nav-permissions.ts`) — den mappningen avgör vad sidomenyn och
 * behörighetsgrindarna släpper igenom, och en felstavad roll ger tyst en tom
 * meny i stället för ett fel.
 */
let cachadeRoller: string[] | null = null;
const SERVERROLLER = STANDARDROLLER;

export function aktivaRoller(): string[] {
  if (!DEV_LÄGE_AKTIVT || typeof window === 'undefined') {
    return SERVERROLLER;
  }
  if (cachadeRoller) {
    return cachadeRoller;
  }

  try {
    const frånUrl = new URLSearchParams(window.location.search).get('roller');
    if (frånUrl) {
      const roller = frånUrl
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean);
      if (roller.length) {
        window.sessionStorage.setItem(ROLL_NYCKEL, JSON.stringify(roller));
        cachadeRoller = roller;
        return cachadeRoller;
      }
    }

    const sparat = window.sessionStorage.getItem(ROLL_NYCKEL);
    if (sparat) {
      const roller = JSON.parse(sparat);
      if (Array.isArray(roller) && roller.length) {
        cachadeRoller = roller;
        return cachadeRoller;
      }
    }
  } catch {
    // Faller tillbaka på standarduppsättningen.
  }

  cachadeRoller = STANDARDROLLER;
  return cachadeRoller;
}

function rollerServerSnapshot(): string[] {
  return SERVERROLLER;
}

export function sättRoller(roller: string[]): void {
  if (!DEV_LÄGE_AKTIVT || typeof window === 'undefined') return;
  cachadeRoller = roller;
  try {
    window.sessionStorage.setItem(ROLL_NYCKEL, JSON.stringify(roller));
  } catch {
    // Ignorera — valet gäller för sidvisningen.
  }
  meddela();
  // Sidomenyn och de flesta vyer läser rollerna vid montering, så hela trädet
  // måste byggas om för att bytet ska betyda något.
  window.location.reload();
}

export function useDevRoller(): string[] {
  return useSyncExternalStore(prenumerera, aktivaRoller, rollerServerSnapshot);
}
